import ChannelHeader from 'anchor-ui/channel-header';
import Button from 'anchor-ui/button';
import { IconHome, IconSettings } from 'anchor-ui/icons';
import { changeUserPrivacy, getProfileFromMetadata, updateUserMetadata } from '../../utils/updateMetadata';
import { metadataInterface } from './../../interfaces/metadataInterface';
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { getSecondaryColor, setActiveColor, setSecondaryColor } from '../../utils/activeColor';
import './styles.css';
import ThemePage from './ThemePage';
import PrivacyPage from './PrivacyPage';
import AccountPage from './AccountPage';
import BlockedUsersPage from './BlockedUsersPage';
import { useState } from 'react';
import LeftSettingsMenu from './LeftSettingsMenu';
import Menu from 'anchor-ui/menu';
import { Functions } from 'firebase/functions';
import VisualsPage from './VisualsPage';
import { UserSettingsInterface } from '../../interfaces/userSettingsInterface';
import { User } from 'firebase/auth';
import { checkUserIsRoyal, checkUserIsVIPOrRoyal } from '../../utils/checkSubscription';

const SettingsPage = ({
  setCurrentChat,
  loggedInUser,
  setAlert,

  userSettings,
  setUserSettings,

  blockedUsers,
  handleBlockUser,
  functions,
  handleLogout,
  user,
  setNeedSubscriptionWarning,
}: {
  setCurrentChat: (chat: any) => void;
  loggedInUser: CometChat.User;
  setAlert: React.Dispatch<React.SetStateAction<{ message: string; type: string; } | null>>;

  userSettings: UserSettingsInterface;
  setUserSettings: (userSettings: UserSettingsInterface) => void;

  blockedUsers: CometChat.User[];
  handleBlockUser: (userId: string) => void;
  functions: Functions;
  handleLogout: () => void;
  user: User | null;
  setNeedSubscriptionWarning: React.Dispatch<React.SetStateAction<React.ReactNode | null>>;
}) => {
  const [userProfile, setUserProfile] = useState(getProfileFromMetadata(loggedInUser));
  const [activePage, setActivePage] = useState('account');

  const [leftMenuOpen, setLeftMenuOpen] = useState(false);

  const handleNavigateHome = () => {
    setCurrentChat({ id: '', icon: '', name: '', isGroup: false });
  };

  // Save changes to the user's metadata
  const handleUpdateUserMetadata = async (newMetadata: metadataInterface) => {
    const updatedMetadata = await updateUserMetadata(functions, newMetadata, loggedInUser);

    if (updatedMetadata && typeof updatedMetadata === 'object') {
      if (
        updatedMetadata.settings &&
        updatedMetadata.settings.themeColor
      ) {
        document.documentElement.style.setProperty('--theme-color', updatedMetadata.settings.themeColor);
      }

      setUserProfile(updatedMetadata);
      setUserSettings({ ...userSettings, ...updatedMetadata.settings });

      setAlert({ message: 'Instellingen zijn bijgewerkt', type: 'success' });
      return true;
    } else {
      setAlert({ message: 'Er is iets misgegaan bij het updaten van je profiel', type: 'error' });
      return false;
    }
  };

  const handleChangeVisibility = async (visibility: string) => {
    try {
      await changeUserPrivacy(functions, loggedInUser, visibility);
      setUserSettings({ ...userSettings, profileVisibility: visibility });

      const newMetadata: metadataInterface = {
        about: {
          gender: userProfile.about.gender,
        },
        settings: {
          ...userSettings,
          profileVisibility: visibility,
        },
      };

      loggedInUser.setMetadata(newMetadata);
      await CometChat.updateCurrentUserDetails(loggedInUser);

      setAlert({ message: 'Privacy instellingen zijn bijgewerkt', type: 'success' });
    } catch (error) {
      setAlert({ message: 'Er is iets misgegaan bij het updaten van je privacy instellingen', type: 'error' });
    }
  };

  // Handle the change of the secondary color
  const handleSetSecondaryColor = (color: string) => {
    const role = loggedInUser?.getRole() ?? "";

    const canAccessVIPColor = checkUserIsVIPOrRoyal(role);
    const canAccessRoyalColors = checkUserIsRoyal(role);

    const updateColor = () => {
      handleUpdateUserMetadata({
        ...userProfile,
        settings: { ...userSettings, secondaryColor: color },
      });

      setSecondaryColor(color);
      document.documentElement.style.setProperty('--secondary-color', color);
    };

    if (color === "white") {
      updateColor();
      return;
    }

    if (color === "#0A0A0A") {
      if (!canAccessVIPColor) {
        setNeedSubscriptionWarning(
          <>
            <b>Deze achtergrondkleur is beschikbaar voor VIP- en Royal-leden.</b><br />
            Met een upgrade naar <b>VIP</b> of <b>Royal</b> krijg je toegang tot exclusieve thema's zoals deze elegante donkerblauwe achtergrond.<br />
          </>
        );
        return;
      }
      updateColor();
      return;
    }

    if (!canAccessRoyalColors) {
      setNeedSubscriptionWarning(
        <>
          <b>Deze achtergrondkleur is alleen beschikbaar voor Royal-leden.</b><br />
          Als Royal-lid ontgrendel je een volledig palet aan exclusieve donkere thema’s om je ervaring volledig naar wens te personaliseren.<br />
          Upgrade vandaag nog en geef je chatomgeving een koninklijke uitstraling.<br />
        </>
      );
      return;
    }

    updateColor();
  };

  // Handle the change of the background image
  const handleSetBackgroundImage = (image: string) => {
    const role = loggedInUser?.getRole() ?? "";

    const canAccessVIPBackgrounds = checkUserIsVIPOrRoyal(role);

    if (!canAccessVIPBackgrounds) {
      setNeedSubscriptionWarning(
        <>
          <b>Deze achtergrond is alleen beschikbaar voor VIP-leden.</b><br />
          Als VIP-lid krijg je toegang tot een exclusieve selectie van prachtige achtergronden om je chatervaring te verbeteren.<br />
          Upgrade vandaag nog en geef je chatomgeving een unieke uitstraling.<br />
        </>
      );
      return;
    }

    handleUpdateUserMetadata({
      ...userProfile,
      settings: { ...userSettings, bgImg: image },
    });
  }

  return (
    <section className="white-page-popover settings-page" style={{ backgroundColor: getSecondaryColor() }}>
      <Menu
        closeMenu={() => setLeftMenuOpen(!leftMenuOpen)}
        open={leftMenuOpen}
        style={{ backgroundColor: 'transparent' }}
      >
        <LeftSettingsMenu
          activeColorState={userSettings.themeColor}
          activePage={activePage}
          setActivePage={setActivePage}
          isSideNav
          generalFontSize={userSettings.generalFontSize}
          handleClose={() => setLeftMenuOpen(false)}
        />
      </Menu>

      <LeftSettingsMenu
        activeColorState={userSettings.themeColor}
        activePage={activePage}
        generalFontSize={userSettings.generalFontSize}
        setActivePage={setActivePage}
      />

      <div
        className='settings-body'
        style={{ backgroundColor: getSecondaryColor() }}
      >
        <ChannelHeader
          name="Instellingen"
          style={{
            backgroundColor: getSecondaryColor(),
            borderColor: getSecondaryColor() !== "white" ? "black" : "#c4c4c4",
          }}
          leftButton={
            <Button
              iconButton
              className='button-toggle-menu'
              onClick={() => setLeftMenuOpen(true)}
            >
              <IconSettings
                color={userSettings.themeColor}
              />
            </Button>
          }
          rightButton={
            <Button iconButton onClick={handleNavigateHome}>
              <IconHome />
            </Button>
          }
        />

        <section className='settings-content'>
          {activePage === 'account' ? (
            <AccountPage
              loggedInUser={loggedInUser}
              setAlert={setAlert}
              functions={functions}
              handleLogout={handleLogout}
              user={user}
            />
          ) :
            activePage === 'privacy' ? (
              <PrivacyPage
                visibility={userSettings.profileVisibility}
                changeVisibility={(visibility) => handleChangeVisibility(visibility)}
                userProfile={userProfile}
                handleUpdateUserMetadata={handleUpdateUserMetadata}
                loggedInUser={loggedInUser}
                setNeedSubscriptionWarning={setNeedSubscriptionWarning}
              />
            ) :
              activePage === 'themes' ? (
                <ThemePage
                  activeColorState={userSettings.themeColor}
                  handleSetActiveColor={(color) => handleUpdateUserMetadata({ ...userProfile, settings: { ...userSettings, themeColor: setActiveColor(color) } })}

                  secondaryColorState={userSettings.secondaryColor}
                  handleSetSecondaryColor={(color) => handleSetSecondaryColor(color)}

                  backgroundImageState={userSettings.bgImg}
                  handleSetBackgroundImage={(image) => handleSetBackgroundImage(image)}

                  loggedInUser={loggedInUser}

                  setAlert={setAlert}
                  functions={functions}

                  setNeedSubscriptionWarning={setNeedSubscriptionWarning}
                />
              ) :
                activePage === 'visuals' ? (
                  <VisualsPage
                    loggedInUser={loggedInUser}
                    userSettings={userSettings}
                    setUserSettings={(settings: UserSettingsInterface) => handleUpdateUserMetadata({ ...userProfile, settings })}
                    setNeedSubscriptionWarning={setNeedSubscriptionWarning}
                  />
                ) : (
                  <BlockedUsersPage
                    blockedUsers={blockedUsers}
                    handleBlockUser={handleBlockUser}
                    userSettings={userSettings}
                    loggedInUser={loggedInUser}
                  />
                )
          }
        </section>

      </div>
    </section>
  );
};

export default SettingsPage;